'use client';
/**
 * thumb-lists (2026-10-24) — 목록 썸네일 한 칸(서버·클라이언트 공용 · 데이터 페치 0).
 *
 *   · 칸 크기 고정(width·height 또는 fluid=aspect-ratio) → CLS 0.
 *   · 이미지: 맨 <img>(/_next/image 금지) · loading=lazy(eager 지정 시만 eager) · decoding=async · width/height 속성.
 *     AI 상상도는 <figure> + 고유 alt + title + 시각 숨김 <figcaption>(RULES#168).
 *   · 이미지 없음 → 현장/글 타일(CSS 칸 · 이미지 요청 0 · aria-hidden).
 *   · 이미지 깨짐(onError · 수화 전 실패 = complete && naturalWidth 0) → 같은 자리에서 타일로 접힌다(CardImg 방식).
 * ⛔ UA 분기 없음 — 모든 방문자·로봇 같은 HTML.
 */
import React, { useEffect, useRef, useState } from 'react';
import { tileTextFor, type ThumbData, type TileSpec } from '@/lib/thumb/resolve';
import './thumb.css';

export interface ThumbProps {
  data: ThumbData | null | undefined;
  /** 칸 크기(px). fluid 면 비율로만 쓴다(width 100%). */
  w: number;
  h: number;
  fluid?: boolean;
  eager?: boolean;
  className?: string;
  /**
   * ui-align2 — 이 미디어 쿼리에 맞는 화면에서는 작은 판(240) 대신 원본(img.full)을 쓴다(<picture><source media>).
   *   예: 블로그 목록 «≥761 카드 ≈330px» 는 원본 · «≤760 행 96px» 는 240 그대로. HTML 은 모든 UA 같고 고르는 건 브라우저(화면 폭)다.
   *   img.full 이 없거나(작은 판 없음 = 이미 원본) 같은 주소면 종전 <img> 하나.
   */
  fullMedia?: string;
}

export function ThumbTile({ tile, w, h }: { tile: TileSpec; w: number; h: number }) {
  const t = tileTextFor(tile, w, h);
  return (
    <span className="kd-tile" data-tone={tile.tone} data-size={Math.min(w, h) <= 48 ? 's' : w >= 120 ? 'l' : 'm'} aria-hidden="true">
      {t.label ? <span className="kd-tile__k">{t.label}</span> : null}
      {/* ui-1101 — 숫자만 있으면(「815」) 무엇인지 몰랐다 → 큰 칸은 「세대」 단위를 붙인다(작은 칸은 폭이 모자라 숫자만). */}
      {t.main ? <b className="kd-tile__n" data-num={tile.numeric && t.main === tile.main ? '' : undefined}>{t.main}{tile.numeric && t.main === tile.main && Math.min(w, h) > 48 ? <span className="kd-tile__u">세대</span> : null}</b> : null}
      {/* ui-1101 — 작은 칸(≤48)은 옆에 붙일 폭이 없어 「세대」 를 숫자 아래 줄로. */}
      {t.main && tile.numeric && t.main === tile.main && Math.min(w, h) <= 48 ? <span className="kd-tile__u2">세대</span> : null}
    </span>
  );
}

export default function Thumb({ data, w, h, fluid, eager, className, fullMedia }: ThumbProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [failed, setFailed] = useState(false);
  const img = data?.img ?? null;
  useEffect(() => {
    const el = ref.current;
    if (el && el.complete && el.naturalWidth === 0) setFailed(true);
  }, [img?.src]);
  if (!data) return null;
  const box: React.CSSProperties = fluid ? { aspectRatio: `${w} / ${h}` } : { width: w, height: h };
  const cls = `kd-thumb${fluid ? ' kd-thumb--fluid' : ''}${className ? ` ${className}` : ''}`;
  if (!img || failed) {
    return (
      <span className={cls} style={box} data-thumb="tile">
        <ThumbTile tile={data.tile} w={w} h={h} />
      </span>
    );
  }
  const full = fullMedia && img.full?.src && img.full.src !== img.src ? img.full.src : null;
  const imgEl = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={img.src}
      alt={img.alt}
      {...(img.ai && img.caption ? { title: img.caption } : {})}
      width={w}
      height={h}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
  const el = full ? (
    <picture>
      <source media={fullMedia} srcSet={full} />
      {imgEl}
    </picture>
  ) : imgEl;
  if (img.ai && img.caption) {
    return (
      <figure className={cls} style={box} data-thumb="generated">
        {el}
        {w >= 96 ? <span className="kd-thumb__ai" aria-hidden="true">AI 상상도</span> : null}
        <figcaption className="kd-thumb__sr">{img.caption}</figcaption>
      </figure>
    );
  }
  return (
    <span className={cls} style={box} data-thumb="photo">
      {el}
    </span>
  );
}
